<script setup>
import { computed, useAttrs } from 'vue';

import { HrSkeleton } from '../primitives';

/**
 * Paragraph — a `<p>` element with a proportional skeleton placeholder for pending/loading states.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Rendering body text that may arrive asynchronously and needs a skeleton while loading
 */
defineOptions({ name: 'HrP' });

const props = defineProps({
  /**
   * Paragraph text content. Falls back to the default slot when null.
   * @replacesSlot default
   * @example The quick brown fox jumps over the lazy dog.
   */
  text: {
    type: String,
    default: null,
  },
  /** Shows a skeleton placeholder sized proportionally to the text length while content loads. */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * CSS class(es) forwarded to the rendered element (skeleton or <p>).
   * Declared explicitly because `class` is a reserved attribute in Vue and
   * won't appear in $props — we need it accessible in the template.
   */
  class: {
    type: [String, Array, Object],
    default: null,
  },
});

const attrs = useAttrs();

// ~8px per character is a reasonable average for a 14–16px body font.
const calcWidth = computed(() => {
  const length = props.text?.length ?? 10;
  return `${Math.max(length, 3) * 8}px`;
});
</script>

<template>
  <hr-skeleton
    v-if="pending"
    type="text"
    :width="calcWidth"
    :class="props.class"
    v-bind="attrs"
  />
  <p
    v-else
    :class="props.class"
    v-bind="attrs"
  >
    <!-- @slot Paragraph content; overrides the text prop when provided. -->
    <slot>{{ text }}</slot>
  </p>
</template>
